/**
 * Genesis combat-lab pack.
 *
 * The six shipped abilities map 1:1 onto the first Genesis weapon families.
 * Tune them in this sandbox (G → sliders, P to freeze a frame), then export.
 * The game later consumes `sim` (authority: range, travel, cooldown, shape)
 * and `settings` (client VFX blob). Damage is never authored here.
 */
import { snapshotSettings } from '../config/settings.js';
import { getAbility } from '../abilities/registry.js';

export const GENESIS_PACK_VERSION = 1;

/** Ability id → Genesis family. Order is the default lab bar. */
export const GENESIS_BINDINGS = Object.freeze([
  {
    vfxId: 'thunder',
    genesisId: 'storm_lance',
    family: 'hitscan',
    districtHint: 'skirmisher',
    role: 'Primary carbine / fast line'
  },
  {
    vfxId: 'ice',
    genesisId: 'frost_lance',
    family: 'projectile',
    districtHint: 'marksman',
    role: 'Bow / travelling front'
  },
  {
    vfxId: 'meteor',
    genesisId: 'cinder_fall',
    family: 'lob',
    districtHint: 'pyre',
    role: 'Grenade / arcing rock'
  },
  {
    vfxId: 'beam',
    genesisId: 'nova_beam',
    family: 'beam',
    districtHint: 'medic',
    role: 'Heal / hold column'
  },
  {
    vfxId: 'snare',
    genesisId: 'voltaic_snare',
    family: 'zone',
    districtHint: 'stalker',
    role: 'Trap / planted circle'
  },
  {
    vfxId: 'glacier',
    genesisId: 'glacial_crown',
    family: 'melee_zone',
    districtHint: 'duelist',
    role: 'Melee burst / close ring'
  }
]);

function simFromAbility(binding) {
  const ability = getAbility(binding.vfxId);
  const block = ability?.settings ?? {};
  return {
    vfxId: binding.vfxId,
    genesisId: binding.genesisId,
    family: binding.family,
    districtHint: binding.districtHint,
    role: binding.role,
    cast: ability?.cast ?? 'line',
    range: block.range ?? 0,
    minRange: block.minRange ?? 0,
    speed: block.speed ?? 0,
    cooldown: block.cooldown ?? 0,
    lifetime: block.lifetime ?? 0,
    zoneRadius: block.zoneRadius ?? 0
  };
}

/** Slim contract + full settings blobs. Safe to check into genesis later. */
export function buildGenesisPack() {
  const snapshot = snapshotSettings();
  return {
    version: GENESIS_PACK_VERSION,
    exportedAt: new Date().toISOString(),
    source: 'threejs-vfx',
    bindings: GENESIS_BINDINGS.map((binding) => ({
      ...simFromAbility(binding),
      settings: snapshot[binding.vfxId] ?? null
    })),
    stage: {
      global: snapshot.global,
      aim: snapshot.aim,
      zone: snapshot.zone,
      post: snapshot.post
    }
  };
}

export function downloadGenesisPack() {
  const pack = buildGenesisPack();
  const blob = new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `genesis-vfx-pack-v${GENESIS_PACK_VERSION}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
  return pack;
}
